// Desk build only: Route Desk can lock the Sign In page's phone field to the
// officer's own employee account, so the officer types only their PIN.
//
// Handshake (agreed with Route Desk, plan 2026-10-12 Piece B change 4):
//   1. this app -> parent:  { type: "he-desk:login-ready" }   on Sign In mount
//   2. parent -> this app:  { type: "he-desk:login-phone", phone: "+256..." }
//      or, for a Route Desk superadmin: { type: "he-desk:login-manual" }
// A phone is accepted only from an allowed Route Desk origin AND from the
// page that embeds us (event.source === window.parent). Anything else is
// ignored. The PIN never travels this way.
//
// NOTE: this is a convenience lock against signing in as the wrong account
// by mistake, not a security boundary.

// Production Route Desk only. A local build (rehearsal, Route Desk dev on
// :5173) adds its dev origin with REACT_APP_DESK_EXTRA_PARENT_ORIGIN in the
// gitignored .env.production.local; the real desk build leaves it unset.
export const ROUTE_DESK_ORIGINS = ["https://routemanager.healthyentrepreneurs.com"];

export const routeDeskOrigins = (extra = process.env.REACT_APP_DESK_EXTRA_PARENT_ORIGIN) =>
  extra ? [...ROUTE_DESK_ORIGINS, extra] : ROUTE_DESK_ORIGINS;
export const LOGIN_READY_TYPE = "he-desk:login-ready";
export const LOGIN_PHONE_TYPE = "he-desk:login-phone";

export const PHONE_PATTERN = /^\+\d{8,15}$/;

// Superadmins on the Route Desk side test with other accounts: instead of a
// phone, Route Desk then sends { type: "he-desk:login-manual" } and the
// Sign In page behaves as it does outside the panel.
export const LOGIN_MANUAL_TYPE = "he-desk:login-manual";

// Returns the message's data object when it comes from an allowed Route Desk
// origin AND from the page that embeds us, else null. The one check shared
// by every message type Route Desk sends.
export const routeDeskData = (event, win) => {
  if (!event || !routeDeskOrigins().includes(event.origin)) return null;
  if (!event.source || event.source !== win.parent) return null;
  const data = event.data;
  return data && typeof data === "object" ? data : null;
};

// Returns the phone number from a valid Route Desk message, else null.
export const phoneFromMessage = (event, win = window) => {
  const data = routeDeskData(event, win);
  if (!data || data.type !== LOGIN_PHONE_TYPE) return null;
  if (typeof data.phone !== "string") return null;
  const phone = data.phone.trim();
  return PHONE_PATTERN.test(phone) ? phone : null;
};

// True for a valid Route Desk "manual sign-in" message (superadmins only).
export const isLoginManualMessage = (event, win = window) => {
  const data = routeDeskData(event, win);
  return !!data && data.type === LOGIN_MANUAL_TYPE;
};

// Posts a message to an embedding Route Desk page. Uses an explicit target
// origin, never "*". Where the browser exposes the embedding origin
// (location.ancestorOrigins, Chrome/Edge) only that one is used; otherwise
// each allowed origin is tried and the browser drops the ones that do not
// match the parent. Does nothing outside a frame. Shared by login-ready and
// cart-status (deskCartStatus.js), so there is one allowlist.
export const postToRouteDesk = (data, win = window) => {
  if (!win.parent || win.parent === win) return;
  const ancestors = win.location && win.location.ancestorOrigins;
  const targets = ancestors && ancestors.length
    ? routeDeskOrigins().filter((origin) => origin === ancestors[0])
    : routeDeskOrigins();
  targets.forEach((origin) => {
    try {
      win.parent.postMessage(data, origin);
    } catch (e) {
      // ignore: not embedded by Route Desk
    }
  });
};

// Tells an embedding Route Desk page that Sign In is ready for the phone.
export const announceLoginReady = (win = window) => postToRouteDesk({ type: LOGIN_READY_TYPE }, win);
